import axiosClient from '@clients/axiosClient';

export type IGetTransactionsDetails = {
    accountId: number;
    statementId?: number;
    transactionId?: string;
    pageOffset?: number;
    pageSize?: number;
    order?: string;
    customerId?: number;
    credit?: boolean;
    authorizationType?: string;
    processingCode?: string;
    transactionTypeId?: number;
    statementPost?: boolean;
    authorizationTrackingId?: string;
    authorizationId?: number;
    eventDateStart?: string;
    eventDateEnd?: string;
};

export type IGetTransactionDetails = {
    accountId: number;
    statementId?: number;
    transactionId?: string;
};

export type IGetTransactionTypes = {
    programId: number;
};

export interface Transactions {
    has_next: boolean;
    items: TransactionItem[];
}

export type TransactionItem = {
    id: number;
    program_id: number;
    account_id: number;
    statement_id: number;
    installment: number;
    number_of_installments: number;
    soft_descriptor: string;
    event_date: string;
    created_at: string;
    processing_code: string;
    processing_description: string;
    user_category: string;
    transaction_group: string;
    transaction_type: TransactionType;
    amount: TransactionAmount[];
    tax: TransactionTax[];
    merchant: TransactionMerchant;
    authorization: TransactionAuthorization;
};

export type TransactionType = {
    id: number;
    description: string;
    credit?: boolean;
    statement_post: boolean;
    balance_impact_id: number;
    category: string;
    posted_transaction: boolean;
};

export type TransactionsType = {
    transaction_type_id: number;
    program_id: number;
    transaction_category_id: number;
    transaction_type_description: string;
};

export type TransactionTypes = {
    pagination: {
        items: TransactionsType[];
        current_page: number;
        per_page: number;
        total_items: number;
        items_on_page: number;
        pages: number;
        is_last_page: boolean;
    };
};

export type TransactionMerchant = {
    id: string;
    type: string;
    name: string;
    city: string;
    state: string;
    category: {
        code: string;
        description: string;
        group_name: string;
        network_group: string;
    };
};

export type TransactionAuthorization = {
    id?: number;
    type: string;
    code: string;
    payment_method_id: string;
    tracking_id: string;
};

export type TransactionAmount = {
    type: string;
    currency: string;
    value: number;
};

export type TransactionTax = {
    type: string;
    value: number;
};

const transactions = () => {
    const client = axiosClient();

    return {
        async getTransactions({
            accountId,
            statementId,
            pageOffset,
            pageSize,
            order,
            customerId,
            credit,
            authorizationType,
            processingCode,
            transactionTypeId,
            statementPost,
            authorizationTrackingId,
            authorizationId,
            eventDateStart,
            eventDateEnd,
        }: IGetTransactionsDetails) {
            const { data } = await client.get<Transactions>(
                'transactions-core/v2/transactions',
                {
                    params: {
                        ...(statementId && { statementId }),
                        order,
                        pageSize,
                        pageOffset,
                        customerId,
                        credit,
                        authorizationType,
                        processingCode,
                        transactionTypeId,
                        statementPost,
                        authorizationTrackingId,
                        authorizationId,
                        eventDateStart,
                        eventDateEnd,
                    },
                    headers: {
                        'x-account-id': accountId,
                    },
                },
            );
            return data;
        },
        async getTransaction({
            accountId,
            transactionId,
        }: IGetTransactionDetails) {
            const { data } = await client.get<TransactionItem>(
                `transactions-core/v2/transactions/${transactionId}`,
                {
                    headers: {
                        'x-account-id': accountId,
                    },
                },
            );
            return data;
        },
        async getTransactionTypes({ programId }: IGetTransactionTypes) {
            const { data } = await client.get<TransactionTypes>(
                `statements-v2/v1/programs-transactions-types`,
                {
                    headers: {
                        'x-program-id': programId,
                    },
                },
            );
            return data;
        },
    };
};

export default transactions;
